<script setup lang="ts">
import { RouterLink } from 'vue-router'
import type { DashboardNeedsAttention } from '@/types/models'
import { linkedinAccountStatusLabels as statusLabels } from '@/lib/linkedinAccountStatus'

const props = defineProps<{
  needsAttention: DashboardNeedsAttention
}>()
</script>

<template>
  <div
    v-if="
      props.needsAttention.unassigned_replies_count === 0
        && props.needsAttention.accounts_needing_reconnect.length === 0
        && props.needsAttention.ramp_gates_awaiting_expansion.length === 0
        && props.needsAttention.predicates_to_tune.length === 0
    "
    class="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-4 font-serif italic text-on-surface-variant"
  >
    Nothing needs you right now.
  </div>

  <div
    v-else
    class="flex flex-col gap-3"
  >
    <RouterLink
      v-if="props.needsAttention.unassigned_replies_count > 0"
      :to="{ name: 'inbox', query: { filter: 'unassigned' } }"
      class="group cursor-pointer rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-4"
    >
      <Badge variant="neutral">
        Inbox
      </Badge>
      <p class="mb-3 mt-2 font-serif leading-relaxed text-on-surface">
        {{ props.needsAttention.unassigned_replies_count }} unassigned inbox
        {{ props.needsAttention.unassigned_replies_count === 1 ? 'thread requires' : 'threads require' }} review.
      </p>
      <span class="text-ui-label text-on-surface-variant underline decoration-outline-variant/50 group-hover:text-on-surface">
        review inbox
      </span>
    </RouterLink>

    <RouterLink
      v-for="account in props.needsAttention.accounts_needing_reconnect"
      :key="account.linkedin_account_id"
      :to="{ name: 'linkedin-accounts' }"
      class="group cursor-pointer rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-4"
    >
      <Badge variant="neutral">
        Reconnect
      </Badge>
      <p class="mb-3 mt-2 font-serif leading-relaxed text-on-surface">
        {{ account.display_name ?? 'An account' }} needs reconnecting
        ({{ statusLabels[account.status] ?? account.status }}).
      </p>
      <span class="text-ui-label text-on-surface-variant underline decoration-outline-variant/50 group-hover:text-on-surface">
        reconnect account
      </span>
    </RouterLink>

    <RouterLink
      v-for="gate in props.needsAttention.ramp_gates_awaiting_expansion"
      :key="gate.campaign_id"
      :to="{ name: 'campaign-builder', params: { campaignId: gate.campaign_id } }"
      class="group cursor-pointer rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-4"
    >
      <Badge variant="neutral">
        Ramp
      </Badge>
      <p class="mb-3 mt-2 font-serif leading-relaxed text-on-surface">
        Campaign "{{ gate.name }}" passed its ramp gate and is ready to expand.
      </p>
      <span class="text-ui-label text-on-surface-variant underline decoration-outline-variant/50 group-hover:text-on-surface">
        adjust volume
      </span>
    </RouterLink>

    <!-- AI-surfaced suggestion — indigo is reserved exclusively for
         machine-surfaced content, per DESIGN.md. jetpack-ui's Badge only
         exposes 6 fixed variants with hardcoded inline-style colors, none
         of which map to this accent, so this one chip is hand-built per
         CLAUDE.md's "anything genuinely not covered by the library"
         fallback clause. -->
    <div
      v-for="predicate in props.needsAttention.predicates_to_tune"
      :key="predicate.branch_sequence_id"
      class="rounded-lg border border-outline-variant/30 border-l-[3px] border-l-ai-accent bg-surface-container-lowest p-4"
    >
      <span class="rounded bg-ai-accent-subtle px-2 py-0.5 text-metadata text-ai-accent">Rule</span>
      <p class="mb-3 mt-2 font-serif leading-relaxed text-on-surface">
        Rule "{{ predicate.name }}" is missing {{ Math.round(predicate.no_match_rate * 100) }}% of evaluations.
      </p>
      <span class="text-ui-label text-on-surface-variant underline decoration-outline-variant/50">
        tune this rule
      </span>
    </div>
  </div>
</template>
